import React from "react";
import { Helmet } from "react-helmet-async";
import { motion, useReducedMotion } from "framer-motion";
import {
  FaUsers,
  FaRobot,
  FaGlobe,
  FaChartLine,
  FaCheckCircle,
} from "react-icons/fa";
import "./About.css";

import { FaYoutube, FaFacebook } from "react-icons/fa";
import { SiX } from "react-icons/si";

const About = () => {
  const shouldReduceMotion = useReducedMotion();

  const sections = [
    {
      title: "Who We Are",
      content:
        "LingoLab leads in translation services, using AI to deliver high-quality results. We help your content reach global audiences with precision.",
      icon: <FaUsers />,
    },
    {
      title: "Innovative Technology",
      content:
        "Our AI preserves the speaker’s voice and syncs lip movements, ensuring translations feel natural and retain the original emotion.",
      icon: <FaRobot />,
    },
    {
      title: "Global Reach",
      content:
        "We translate over 80 languages, from niche dialects to widely spoken ones, connecting you to diverse audiences worldwide.",
      icon: <FaGlobe />,
    },
    {
      title: "Unlock New Opportunities",
      content:
        "Translating into popular languages expands your audience, boosting viewership and monetization potential with LingoLab.",
      icon: <FaChartLine />,
    },
    {
      title: "Our Commitment to Quality",
      content:
        "We ensure top-quality translations, declining rare cases like overlapping voices or complex lip-syncing to meet our standards. Most videos translate seamlessly.",
      icon: <FaCheckCircle />,
    },
  ];

  return (
    <div className="about-container">
      <Helmet>
        <title>About LingoLab – AI Video Translation & Localization</title>
        <meta
          name="description"
          content="Learn about LingoLab, the leader in AI-powered video translation and localization. Discover our technology, global reach, and commitment to quality."
        />
        <meta
          name="keywords"
          content="About LingoLab, AI translation, video localization, multilingual video, company, technology, quality"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://lingolab.ca/about" />
        <meta
          property="og:title"
          content="About LingoLab – AI Video Translation"
        />
        <meta
          property="og:description"
          content="Meet LingoLab: revolutionizing video translation and localization with advanced AI. Learn about our mission, technology, and global impact."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lingolab.ca/about" />
        {/* --- Twitter Card tags --- */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta
          name="twitter:title"
          content="About LingoLab – AI Video Translation"
        />
        <meta
          name="twitter:description"
          content="Meet LingoLab: revolutionizing video translation and localization with advanced AI. Learn about our mission, technology, and global impact."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <motion.header
        className="about-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.4,
          ease: "easeOut",
        }}
      >
        <h1 className="about-title">About LingoLab</h1>
        <p className="about-subtitle">Revolutionizing Translation with AI</p>
        <div className="header-divider"></div>
      </motion.header>

      <div className="about-sections">
        {sections.map((section, index) => (
          <motion.div
            key={index}
            className="about-card"
            role="article"
            aria-labelledby={`section-title-${index}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              duration: shouldReduceMotion ? 0 : 0.3,
              delay: index * 0.1,
            }}
            whileHover={
              shouldReduceMotion
                ? {}
                : { y: -5, boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)" }
            }
          >
            <div className="about-icon">{section.icon}</div>
            <div className="about-content">
              <h2 className="about-section-title" id={`section-title-${index}`}>
                {section.title}
              </h2>
              <p className="about-section-text">{section.content}</p>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="about-social">
        <h2 className="about-social-title">Follow Us</h2>
        <div className="about-social-icons">
          <a
            href="https://youtube.com/@LingoLabAI"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="YouTube"
            className="about-social-link"
          >
            <FaYoutube />
          </a>
          <a
            href="https://twitter.com/LingoLabAI"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="X (Twitter)"
            className="about-social-link"
          >
            <SiX />
          </a>
          <a
            href="https://facebook.com/LingoLabAI"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="about-social-link"
          >
            <FaFacebook />
          </a>
        </div>
      </div>
    </div>
  );
};

export default About;
